import React, { useState, useRef, useEffect } from 'react';
import { 
  User, 
  Calendar, 
  Clipboard, 
  Share, 
  UserCircle,
  Users,
  Mail,
  Bell,
  LogOut
} from 'lucide-react';
import { useAuth } from '../../../state/auth/useAuth';
import Logo from '../../../components/Logo';

const iconMap = {
  'user': User,
  'calendar': Calendar,
  'clipboard': Clipboard,
  'share': Share,
  'user-circle': UserCircle,
  'users': Users
};

const TopNavBar = ({ user, tabs, activeTab, onTabChange }) => {
  const { logout } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileRef]);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await logout();
    }
  };

  return (
    <div className="bg-white border-b-2 border-gray-200 shadow-sm" style={{ fontFamily: 'Poppins' }}>
      <div className="flex items-center justify-between px-6 py-3">
        {/* Logo Section */}
        <div className="flex items-center min-w-[160px] ml-5">
          <Logo size="lg" showText={true} />
        </div>

        {/* Navigation Tabs - Centered */}
        <nav className="flex items-center bg-gray-100 rounded-full p-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`
                  px-4 py-2 text-lg font-medium transition-all duration-200 rounded-full whitespace-nowrap
                  ${isActive 
                    ? 'bg-[#2D869C] text-white shadow-sm' 
                    : 'text-gray-600 hover:text-[#2D869C] hover:bg-gray-200'
                  }
                `}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* Right Side - Icons and User Profile */}
        <div className="flex items-center space-x-2 min-w-[160px] justify-end">
         

          {/* User Profile Dropdown */}
          <div className="relative" ref={profileRef}>
            <button 
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center focus:outline-none"
            >
              <div className="w-10 h-10 mr-5 rounded-full overflow-hidden border-2 border-[#2D869C] shadow-sm">
                {user?.avatar ? (
                  <img src={user.avatar} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#9DC9D4]/30 flex items-center justify-center text-[#2D869C]">
                    <User size={18} />
                  </div>
                )}
              </div>
            </button>

            {isProfileOpen && (
              <div className="absolute mr-5 right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
                   {user?.specialization && (
                    <div className=" inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold bg-[#9DC9D4]/30 text-[#2D869C]">
                      {user.specialization}
                    </div>
                  )}
                  {user?.role && !user?.specialization && (
                    <div className=" inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold bg-[#9DC9D4]/30 text-[#2D869C]">
                      {user.role}
                    </div>
                  )}
                  <p className="mt-2 text-xl font-semibold text-gray-900 truncate">{user?.name || 'Doctor'}</p>
                  <p className="text-md text-gray-500 truncate font-medium mt-0.5">{user?.email || 'No email'}</p>
                 
                </div>
                <div className="py-2">
                  <button 
                    onClick={() => {
                      onTabChange('profile');
                      setIsProfileOpen(false);
                    }} 
                    className="w-full text-left px-5 py-2.5 text-md text-gray-700 hover:bg-gray-50 flex items-center transition-colors font-medium"
                  >
                    <User size={20} className="mr-3 text-gray-500" />
                    My Profile
                  </button>
                  <button 
                    onClick={handleLogout}
                    className="w-full text-left px-5 py-2.5 text-md text-red-600 hover:bg-red-50 flex items-center transition-colors font-semibold border-t border-gray-200 mt-1 pt-3"
                  >
                    <LogOut size={20} className="mr-3" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopNavBar;
